/**
 * Single authoritative file upload client using raw XMLHttpRequest.
 * Bypasses all Axios interceptors and transforms.
 * Never manually sets Content-Type — browser adds multipart boundary.
 */

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
var MAX_FILE_SIZE = 25 * 1024 * 1024;

function errorText(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(errorText).filter(Boolean).join('; ');
  if (value && typeof value === 'object') return errorText(value.message || value.msg || value.detail || value.error);
  return '';
}

export async function uploadFile(file, purpose) {
  if (!(file instanceof File)) {
    throw new Error("No valid file was selected.");
  }
  if (file.size === 0) {
    throw new Error("The selected file is empty.");
  }
  if (file.size > MAX_FILE_SIZE) throw new Error('The file exceeds the 25 MB upload limit. Choose a smaller file.');
  if (purpose === 'product_image' && file.type && file.type !== 'application/octet-stream' &&
      !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
    throw new Error('Choose a JPEG, PNG, WebP or GIF product image.');
  }
  if (!BACKEND_URL) throw new Error('The upload service address is not configured.');

  var formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("purpose", purpose || 'general');

  var traceId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);

  console.info("UPLOAD_REQUEST", {
    traceId: traceId,
    name: file.name,
    type: file.type,
    size: file.size,
    purpose: purpose || "general",
    formDataHasFile: formData.has("file"),
  });

  // Use raw XMLHttpRequest to completely bypass any Axios interceptors/transforms
  return new Promise(function(resolve, reject) {
    var xhr = new XMLHttpRequest();
    xhr.open("POST", BACKEND_URL.replace(/\/$/, '') + "/api/admin/files/upload");
    xhr.setRequestHeader("Authorization", "Bearer " + (localStorage.getItem("admin_token") || ""));
    xhr.setRequestHeader("X-Upload-Trace-ID", traceId);
    // DO NOT set Content-Type — browser adds multipart boundary automatically

    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText);
          var url = data.url || data.file_url || data.download_url || "";

          // Resolve against the same configured API that owns the file, not the editor's host.
          if (url && url.startsWith("/api/files/")) {
            url = BACKEND_URL.replace(/\/$/, '') + url;
          }
          if (data.success === false || !url || !/^https?:\/\//i.test(url) || !(data.file_id || data.id)) {
            reject(new Error('The upload service did not return a saved file reference. Your previous image has been kept.'));
            return;
          }

          console.info("UPLOAD_SUCCESS", { traceId: traceId, fileId: data.file_id, url: url });
          resolve({
            id: data.file_id || data.id || null,
            url: url,
            filename: data.filename || file.name,
            mimeType: data.mime_type || data.content_type || file.type,
            size: data.size || file.size,
          });
        } catch (e) {
          reject(new Error('The upload service returned an invalid response. Your previous image has been kept.'));
        }
      } else {
        var errMsg = xhr.status === 413 ? 'The file exceeds the upload size limit. Choose a smaller file.' :
          (xhr.status === 401 || xhr.status === 403) ? 'Your session cannot authorise this upload. Sign in again before retrying; your saved offer is unchanged.' :
          'The upload service returned HTTP ' + xhr.status + '. Your previous image has been kept.';
        try {
          var errData = JSON.parse(xhr.responseText);
          if (xhr.status !== 401 && xhr.status !== 403) errMsg = errorText(errData.detail || errData.message) || errMsg;
        } catch (e) {}
        console.error("UPLOAD_FAILED", { traceId: traceId, status: xhr.status, error: errMsg });
        reject(new Error(errMsg + ' (Upload reference: ' + traceId + ')'));
      }
    };

    xhr.onerror = function() {
      reject(new Error('The upload service could not be reached. Check your connection and retry. Your previous image has been kept.'));
    };

    xhr.ontimeout = function() {
      reject(new Error('The upload timed out before it could be confirmed. Your previous image has been kept; please retry.'));
    };

    xhr.timeout = 120000;
    xhr.send(formData);
  });
}
